import { NSARouterMakerResult } from "namirasoft-account-react";
import { WiperInputRow, WiperRow, WiperType, WiperTimeType } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxInteger, NSBoxEnum, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface WiperEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

interface WiperEditPageState
{
    type: WiperType | null;
}

export default class WiperEditPage extends Component<WiperEditPageProps, WiperEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Type = createRef<NSBoxEnum<WiperType>>();
    NSBoxEnum_TimeType = createRef<NSBoxEnum<WiperTimeType>>();
    NSBoxInteger_TimeValue = createRef<NSBoxInteger>();
    NSBoxInteger_Count = createRef<NSBoxInteger>();
    NSBoxString_Description = createRef<NSBoxString>();

    private pendingTimeType: WiperTimeType | null = null;
    private pendingTimeValue: number | null = null;
    private pendingCount: number | null = null;

    constructor(props: WiperEditPageProps)
    {
        super(props);
        this.state = { type: null };
        this.onTypeChanged = this.onTypeChanged.bind(this);
    }

    private onTypeChanged()
    {
        let raw = this.NSBoxEnum_Type.current?.getValueEnumOne(WiperType, null as unknown as WiperType, false) as WiperType | null | undefined;
        let next: WiperType | null = raw ?? null;
        if (next !== this.state.type)
            this.setState({ type: next }, () => this.applyPending());
    }

    private applyPending()
    {
        if (this.state.type === WiperType.Time)
        {
            if (this.pendingTimeType !== null)
            {
                this.NSBoxEnum_TimeType.current?.setValue(this.pendingTimeType);
                this.pendingTimeType = null;
            }
            if (this.pendingTimeValue !== null)
            {
                this.NSBoxInteger_TimeValue.current?.setValue(this.pendingTimeValue);
                this.pendingTimeValue = null;
            }
        }
        else if (this.state.type === WiperType.Count)
        {
            if (this.pendingCount !== null)
            {
                this.NSBoxInteger_Count.current?.setValue(this.pendingCount);
                this.pendingCount = null;
            }
        }
    }

    override render()
    {
        let isTime = this.state.type === WiperType.Time;
        let isCount = this.state.type === WiperType.Count;

        return (
            <InferenceLayout<WiperRow, WiperInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.wiper.edit : InferenceInfo.wiper.create }}
                pages={{
                    entity: InferenceEntities.wiper(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): WiperInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = (this.NSBoxEnum_Type.current?.getValueOne() as WiperType | null | undefined) ?? Object.values(WiperType)[0] as WiperType;
                                        let time_type: WiperTimeType | null = null;
                                        let time_value: number | null = null;
                                        let count: number | null = null;
                                        if (type === WiperType.Time)
                                        {
                                            time_type = (this.NSBoxEnum_TimeType.current?.getValueOne() as WiperTimeType | null | undefined) ?? null;
                                            time_value = this.NSBoxInteger_TimeValue.current?.getValue() ?? null;
                                        }
                                        else if (type === WiperType.Count)
                                        {
                                            count = this.NSBoxInteger_Count.current?.getValue() ?? null;
                                        }
                                        let description = this.NSBoxString_Description.current?.getValue() ?? null;
                                        return {
                                            workspace_id: null,
                                            name,
                                            type,
                                            time_type,
                                            time_value,
                                            count,
                                            description,
                                            wiper_category: [],
                                            wiper_field: [],
                                            wiper_tag: [],
                                        };
                                    },
                                    setRow: (row: WiperRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValue(row.type);
                                        this.NSBoxString_Description.current?.setValue(row.description);

                                        if (row.type === WiperType.Time)
                                        {
                                            this.pendingTimeType = row.time_type;
                                            this.pendingTimeValue = row.time_value;
                                            this.pendingCount = null;
                                        }
                                        else if (row.type === WiperType.Count)
                                        {
                                            this.pendingCount = row.count;
                                            this.pendingTimeType = null;
                                            this.pendingTimeValue = null;
                                        }
                                        else
                                        {
                                            this.pendingTimeType = null;
                                            this.pendingTimeValue = null;
                                            this.pendingCount = null;
                                        }
                                        this.setState({ type: row.type }, () => this.applyPending());
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-name" }}
                    />
                    <NSBoxEnum<WiperType>
                        ref={this.NSBoxEnum_Type}
                        title="Type"
                        required
                        multiple={false}
                        getEnumObject={() => WiperType}
                        onChanged={this.onTypeChanged}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-type" }}
                    />
                    {isTime && (
                        <NSRow style={{ flexWrap: "nowrap" }}>
                            <NSBoxInteger
                                ref={this.NSBoxInteger_TimeValue}
                                title="Time Value"
                                required
                                style={{ width: NSBox.width.one }}
                                info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-time-value" }}
                            />
                            <NSBoxEnum<WiperTimeType>
                                ref={this.NSBoxEnum_TimeType}
                                title="Unit"
                                required
                                multiple={false}
                                getEnumObject={() => WiperTimeType}
                                style={{ width: NSBox.width.one }}
                                info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-unit" }}
                            />
                        </NSRow>
                    )}
                    {isCount && (
                        <NSBoxInteger
                            ref={this.NSBoxInteger_Count}
                            title="Count"
                            required
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-count" }}
                        />
                    )}
                    <NSBoxString
                        ref={this.NSBoxString_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/wiper/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}